import { tz } from '@date-fns/tz'
import { differenceInCalendarDays } from 'date-fns'

import { CueStartMode, CueType } from '~shared/rundown/cue'
import type { Cue } from '~shared/rundown/cue'
import { addDays, applyDate, getStartOfDay } from '~shared/timeutils'
import type { Runner, RundownCueOrderItem } from '~shared/types'
import { createTimestamps, flattenCueOrderItems } from '~shared/utils'

/**
 * Backtime planning for the gap/overlap "Fix" menu.
 *
 * Ported from `~shared/utils` (`computeBacktime` and friends). The client is
 * the only consumer now that the v1 `fix-gap` endpoint is gone, so the planner
 * lives beside its caller instead of in a shared package.
 *
 * The timeline PRIMITIVES stay shared: `createTimestamps` and
 * `flattenCueOrderItems` are still imported. Only the planning layer on top of
 * them is local. That boundary matters: `createTimestamps` defines the
 * FIXED-cue date convention this module has to mirror (see
 * `moveHardStartInstant`), and reimplementing it here would guarantee drift.
 */

/**
 * Everything the planner needs to resolve a timeline. Declared here rather than
 * imported: the planner is client-owned, so its input shape is the client's to
 * change without touching `~shared/utils`.
 */
export interface BacktimeContext {
  cues: readonly Cue[]
  cueOrder: RundownCueOrderItem[]
  rundownStartTime: Date
  timezone?: string
  runner?: Runner | null
}

/** Signed gap (>0) / overlap (<0) at a hard-start cue, plus what governs it. */
export interface BacktimeAnchor {
  /** Signed milliseconds between the previous cue's end and the anchor's start. */
  differenceMs: number
  anchorStartMs: number
  governingStartMs: number
  governingCueId: string | null
  governingIsFirstCue: boolean
}

export type BacktimeStrategy =
  | 'move_this_hard_start'
  | 'move_preceding_hard_start'
  | 'adjust_cue_duration'

export type BacktimeError =
  | 'no_governing_cue'
  | 'adjust_cue_id_required'
  | 'adjust_cue_not_in_segment'
  | 'adjust_would_go_negative'

export type BacktimeWrite =
  | {
      kind: 'cue_start_time'
      cueId: string
      startTime: Date
      /**
       * Which show-day the cue lands on, relative to the first day. A FIXED
       * cue's position is `(time-of-day, startDatePlus)`. `createTimestamps`
       * keeps only those two and re-derives the date, so a shift across
       * midnight has to update this or the engine snaps the cue back onto its
       * original day.
       */
      startDatePlus: number
      isFirstCue: boolean
    }
  | {
      kind: 'cue_duration'
      cueId: string
      cueDuration: number
      isFirstCue: boolean
    }

/**
 * Resolve the gap/overlap at `anchorCueId` off the computed timeline, plus the
 * governing hard start. Returns null when the anchor isn't a FIXED cue with a
 * gap/overlap to fix, including when it already lines up exactly.
 */
export function resolveBacktimeAnchor(
  ctx: BacktimeContext,
  anchorCueId: string,
): BacktimeAnchor | null {
  const anchor = ctx.cues.find((c) => c.id === anchorCueId)
  if (!anchor || anchor.type !== CueType.CUE) return null
  if (anchor.startMode !== CueStartMode.FIXED) return null

  const timestamps = createTimestamps(
    ctx.cues,
    ctx.cueOrder,
    ctx.runner ?? null,
    ctx.rundownStartTime,
    {
      timezone: ctx.timezone ?? 'UTC',
    },
  )
  const anchorTs = timestamps.cues[anchorCueId]
  if (!anchorTs) return null

  const orderedIds = flattenCueOrderItems(ctx.cueOrder)
  const anchorIndex = orderedIds.indexOf(anchorCueId)
  const typeById = new Map(ctx.cues.map((c) => [c.id, c.type]))
  const startModeById = new Map(ctx.cues.map((c) => [c.id, c.startMode]))

  // Walk up to the nearest preceding CUE to find where the run above ends.
  let prevEndMs: number | null = null
  for (let i = anchorIndex - 1; i >= 0; i--) {
    const id = orderedIds[i]
    if (typeById.get(id) !== CueType.CUE) continue
    const ts = timestamps.cues[id]
    if (!ts) continue
    prevEndMs = ts.actual.start.getTime() + ts.actual.duration
    break
  }
  if (prevEndMs == null) return null

  const anchorStartMs = anchorTs.actual.start.getTime()
  const differenceMs = anchorStartMs - prevEndMs
  // Nothing to fix when the anchor already meets the run above. Without this
  // every strategy plans a write that changes nothing, and the menu offers to
  // "move" a cue by 0ms.
  if (differenceMs === 0) return null

  let governingCueId: string | null = null
  for (let i = anchorIndex - 1; i >= 0; i--) {
    const id = orderedIds[i]
    if (typeById.get(id) !== CueType.CUE) continue
    if (startModeById.get(id) === CueStartMode.FIXED) {
      governingCueId = id
      break
    }
  }
  const firstCueId =
    orderedIds.find((id) => typeById.get(id) === CueType.CUE) ?? null
  if (governingCueId == null) governingCueId = firstCueId

  // Guarded like every other timeline read above: the governing cue may be
  // absent from `timestamps.cues` (skipped, or filtered out of the tree).
  const governingTs = governingCueId ? timestamps.cues[governingCueId] : null

  return {
    differenceMs,
    anchorStartMs,
    governingStartMs:
      governingTs?.actual.start.getTime() ?? ctx.rundownStartTime.getTime(),
    governingCueId,
    governingIsFirstCue:
      governingCueId != null && governingCueId === firstCueId,
  }
}

/**
 * CUE-type cues that feed the anchor's gap: those in run order between the
 * governing hard start (inclusive) and the anchor (exclusive). Cues above the
 * governing hard start can't affect the anchor, so absorbing into them would be
 * a silent no-op, so they're excluded here.
 */
export function backtimeSegment(
  ctx: BacktimeContext,
  anchorCueId: string,
  governingCueId: string | null,
) {
  const orderedIds = flattenCueOrderItems(ctx.cueOrder)
  const anchorIndex = orderedIds.indexOf(anchorCueId)
  if (anchorIndex <= 0) return []
  const fromIndex =
    governingCueId != null ? orderedIds.indexOf(governingCueId) : 0
  const byId = new Map(ctx.cues.map((c) => [c.id, c]))
  return orderedIds
    .slice(Math.max(fromIndex, 0), anchorIndex)
    .map((id) => byId.get(id))
    .filter((c) => c != null && c.type === CueType.CUE)
}

/**
 * Shift a FIXED cue by `differenceMs`, expressed the way the engine stores it.
 *
 * `createTimestamps` DISCARDS a FIXED cue's stored date and re-derives it as
 * `getStartOfDay(showStart) + startDatePlus`, keeping only the wall-clock time.
 * A planned write therefore can't be a raw instant: `new Date(ms ± difference)`
 * survives the round-trip only while the shift stays inside one day. Cross
 * midnight and the engine snaps the cue back onto its original day, so the gap
 * never closes; cross a DST boundary and the wall-clock lands an hour off.
 *
 * So compute the shifted instant on the cue's *current* day, then re-express it
 * as the `(startTime, startDatePlus)` pair the engine will read back. Both
 * anchor on `getStartOfDay`, matching `createTimestamps` exactly. Anchoring on
 * the raw `rundownStartTime` instead puts every write out by the show's
 * start-of-day offset.
 */
function moveHardStart(params: {
  cueStartTime: Date
  startDatePlus: number
  differenceMs: number
  rundownStartTime: Date
  timezone: string
}): { startTime: Date; startDatePlus: number } {
  const {
    cueStartTime,
    startDatePlus,
    differenceMs,
    rundownStartTime,
    timezone,
  } = params
  const startOfFirstDay = getStartOfDay(rundownStartTime, { timezone })

  // Where the cue currently sits on the timeline, then shifted.
  const currentDay = addDays(startOfFirstDay, startDatePlus, { timezone })
  const current = applyDate(cueStartTime, currentDay, { timezone })
  const shifted = new Date(current.getTime() - differenceMs)

  // Re-express as (time-of-day, day offset), since the shift may have crossed
  // midnight in either direction, which changes the day the cue belongs to.
  return {
    startTime: shifted,
    startDatePlus: differenceInCalendarDays(shifted, startOfFirstDay, {
      in: tz(timezone),
    }),
  }
}

/**
 * Plan the single write a strategy performs against a resolved anchor. Returns
 * `{ write }` or `{ error }` (caller maps the error to UI state).
 */
export function planBacktime(params: {
  ctx: BacktimeContext
  anchorCueId: string
  anchor: BacktimeAnchor
  strategy: BacktimeStrategy
  absorbCueId?: string
}): { write: BacktimeWrite } | { error: BacktimeError } {
  const { ctx, anchorCueId, anchor, strategy, absorbCueId } = params
  const { differenceMs } = anchor
  const timezone = ctx.timezone ?? 'UTC'

  if (strategy === 'move_this_hard_start') {
    const anchorCue = ctx.cues.find((c) => c.id === anchorCueId)
    if (!anchorCue?.startTime) return { error: 'no_governing_cue' }
    const moved = moveHardStart({
      cueStartTime: anchorCue.startTime,
      startDatePlus: anchorCue.startDatePlus ?? 0,
      differenceMs,
      rundownStartTime: ctx.rundownStartTime,
      timezone,
    })
    return {
      write: {
        kind: 'cue_start_time',
        cueId: anchorCueId,
        startTime: moved.startTime,
        startDatePlus: moved.startDatePlus,
        isFirstCue: false,
      },
    }
  }

  if (strategy === 'move_preceding_hard_start') {
    if (!anchor.governingCueId) return { error: 'no_governing_cue' }
    const governingCue = ctx.cues.find((c) => c.id === anchor.governingCueId)
    if (!governingCue?.startTime) return { error: 'no_governing_cue' }
    // The governing cue is FIXED too, so it round-trips through the same
    // `createTimestamps` path and needs the same treatment. Shifting it LATER
    // by `differenceMs` closes the gap, hence the negated difference.
    const moved = moveHardStart({
      cueStartTime: governingCue.startTime,
      startDatePlus: governingCue.startDatePlus ?? 0,
      differenceMs: -differenceMs,
      rundownStartTime: ctx.rundownStartTime,
      timezone,
    })
    return {
      write: {
        kind: 'cue_start_time',
        cueId: anchor.governingCueId,
        startTime: moved.startTime,
        startDatePlus: moved.startDatePlus,
        isFirstCue: anchor.governingIsFirstCue,
      },
    }
  }

  if (!absorbCueId) return { error: 'adjust_cue_id_required' }
  const target = backtimeSegment(ctx, anchorCueId, anchor.governingCueId).find(
    (c) => c.id === absorbCueId,
  )
  if (!target) return { error: 'adjust_cue_not_in_segment' }
  const cueDuration = target.duration + differenceMs
  if (cueDuration < 0) return { error: 'adjust_would_go_negative' }
  return {
    write: {
      kind: 'cue_duration',
      cueId: absorbCueId,
      cueDuration,
      isFirstCue:
        anchor.governingIsFirstCue && absorbCueId === anchor.governingCueId,
    },
  }
}
